import { FC, useEffect, useState } from "react";
import "./ReuseHarmonisationShimmer.scss";

const TOTAL_SECONDS = 45;

// Staged copy shown while the backend queue worker runs reuse/harmonization analysis against
// the initiative's concepts (mirrors CqGenerationShimmer's stage-progression pattern).
const STAGES = [
  { label: "Scanning existing ontology sources…", icon: "🔍", duration: 8 },
  { label: "Matching concepts for reuse…", icon: "🧬", duration: 10 },
  { label: "Scoring coverage & confidence…", icon: "📊", duration: 9 },
  { label: "Identifying harmonisation candidates…", icon: "🔀", duration: 9 },
  { label: "Finalizing recommendations…", icon: "✨", duration: 9 }
];

interface ReuseHarmonisationShimmerProps {
  // Overrides the staged copy with a single message once the backend confirms the
  // analysis is actually in progress (vs. just queued/about to start).
  statusMessage?: string;
}

const ReuseHarmonisationShimmer: FC<ReuseHarmonisationShimmerProps> = ({
  statusMessage
}) => {
  const [activeStage, setActiveStage] = useState(0);
  const [secondsElapsed, setSecondsElapsed] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setSecondsElapsed((prev) =>
        prev < TOTAL_SECONDS ? prev + 1 : TOTAL_SECONDS
      );
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    let cumulative = 0;
    for (let i = 0; i < STAGES.length; i++) {
      cumulative += STAGES[i].duration;
      if (secondsElapsed < cumulative) {
        setActiveStage(i);
        return;
      }
    }
    setActiveStage(STAGES.length - 1);
  }, [secondsElapsed]);

  const progressPercent = (secondsElapsed / TOTAL_SECONDS) * 100;

  return (
    <div className="reuse-harmonisation-shimmer">
      <div className="reuse-harmonisation-shimmer__card">
        <div className="reuse-harmonisation-shimmer__header">
          <div className="reuse-harmonisation-shimmer__header-left">
            <div className="reuse-harmonisation-shimmer__pulse-ring">
              <span className="reuse-harmonisation-shimmer__status-dot" />
            </div>
            <div>
              <h2 className="reuse-harmonisation-shimmer__heading">
                Running Reuse &amp; Harmonisation Analysis
              </h2>
              <p className="reuse-harmonisation-shimmer__subtitle">
                {statusMessage ??
                  "Comparing your concepts against existing ontologies to recommend reuse and harmonisation decisions."}
              </p>
            </div>
          </div>
        </div>

        <div className="reuse-harmonisation-shimmer__progress">
          <div
            className="reuse-harmonisation-shimmer__progress-fill"
            style={{ width: `${Math.min(progressPercent, 100)}%` }}
          />
        </div>

        <div className="reuse-harmonisation-shimmer__steps">
          {STAGES.map((stage, i) => {
            const isDone = i < activeStage;
            const isActive = i === activeStage;
            return (
              <div
                key={i}
                className={`reuse-harmonisation-shimmer__step ${
                  isDone
                    ? "reuse-harmonisation-shimmer__step--done"
                    : isActive
                    ? "reuse-harmonisation-shimmer__step--active"
                    : ""
                }`}
              >
                <div className="reuse-harmonisation-shimmer__step-icon">
                  {isDone ? (
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <defs>
                        <linearGradient
                          id="reuseHarmonisationCheckGrad"
                          x1="0"
                          y1="0"
                          x2="20"
                          y2="20"
                          gradientUnits="userSpaceOnUse"
                        >
                          <stop offset="0%" stopColor="#4F7BF7" />
                          <stop offset="100%" stopColor="#E84FAD" />
                        </linearGradient>
                      </defs>
                      <circle
                        cx="10"
                        cy="10"
                        r="10"
                        fill="url(#reuseHarmonisationCheckGrad)"
                      />
                      <path
                        d="M6 10L9 13L14 7"
                        stroke="#ffffff"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  ) : (
                    <span className="reuse-harmonisation-shimmer__step-emoji">
                      {stage.icon}
                    </span>
                  )}
                </div>
                <div className="reuse-harmonisation-shimmer__step-content">
                  <span className="reuse-harmonisation-shimmer__step-label">
                    {stage.label}
                  </span>
                  {isActive && (
                    <div className="reuse-harmonisation-shimmer__step-bar">
                      <div
                        className="reuse-harmonisation-shimmer__step-bar-fill"
                        style={{ animationDuration: `${stage.duration}s` }}
                      />
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ReuseHarmonisationShimmer;
